import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import {
  getAdminStaff, createAdminStaff, updateAdminStaff,
  deleteAdminStaff, resetAdminStaffPass,
} from '../../services/adminService';

const PERMISSION_LABELS = {
  doctors:      { label: 'Doctors Management',    icon: 'fa-user-doctor',    desc: 'View & manage doctor accounts' },
  patients:     { label: 'Patient Management',    icon: 'fa-users',           desc: 'View patient accounts & data' },
  appointments: { label: 'Appointments Overview', icon: 'fa-calendar-check', desc: 'View all appointments' },
  payments:     { label: 'Payment Management',    icon: 'fa-indian-rupee-sign',desc: 'Approve & manage payments' },
};

function StaffModal({ staff, onClose, onSave }) {
  const isEdit = !!staff;
  const [form, setForm] = useState({
    name:     staff?.name     || '',
    username: staff?.username || '',
    password: '',
    permissions: {
      doctors:      staff?.permissions?.doctors      || false,
      patients:     staff?.permissions?.patients     || false,
      appointments: staff?.permissions?.appointments || false,
      payments:     staff?.permissions?.payments     || false,
    },
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setPerm = (k) => setForm(p => ({ ...p, permissions: { ...p.permissions, [k]: !p.permissions[k] } }));

  const handleSubmit = async () => {
    if (!form.name.trim())     { toast.error('Name required'); return; }
    if (!form.username.trim()) { toast.error('Username required'); return; }
    if (!isEdit && !form.password) { toast.error('Password required'); return; }
    if (form.password && form.password.length < 6) { toast.error('Password must be 6+ characters'); return; }
    if (!Object.values(form.permissions).some(Boolean)) { toast.error('Grant at least one permission'); return; }
    setLoading(true);
    try {
      const payload = { name: form.name, username: form.username, permissions: form.permissions };
      if (!isEdit) payload.password = form.password;
      if (isEdit) await updateAdminStaff(staff._id, payload);
      else        await createAdminStaff(payload);
      toast.success(isEdit ? 'Staff updated!' : 'Staff account created!');
      onSave();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-text-primary">{isEdit ? 'Edit Staff' : 'Add Admin Staff'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-surface flex items-center justify-center">
            <i className="fa-solid fa-xmark text-text-secondary" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="input-label">Full Name <span className="text-danger">*</span></label>
            <input value={form.name} onChange={e => set('name', e.target.value)} className="input" placeholder="Staff Name" />
          </div>
          <div>
            <label className="input-label">Username <span className="text-danger">*</span></label>
            <input value={form.username} onChange={e => set('username', e.target.value)}
              className="input" placeholder="login_username" disabled={isEdit} />
            {isEdit && <p className="text-[10px] text-text-muted mt-1">Username cannot be changed after creation</p>}
          </div>
          {!isEdit && (
            <div>
              <label className="input-label">Password <span className="text-danger">*</span></label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                className="input" placeholder="Min 6 characters" />
            </div>
          )}
        </div>

        {/* Permissions */}
        <div>
          <p className="input-label mb-2">Section Access <span className="text-danger">*</span></p>
          <div className="space-y-2">
            {Object.entries(PERMISSION_LABELS).map(([key, { label, icon, desc }]) => (
              <button key={key} onClick={() => setPerm(key)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  form.permissions[key] ? 'border-primary bg-primary-light' : 'border-border bg-surface'
                }`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  form.permissions[key] ? 'bg-primary' : 'bg-white border border-border'
                }`}>
                  <i className={`fa-solid ${icon} text-sm ${form.permissions[key] ? 'text-white' : 'text-text-muted'}`} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${form.permissions[key] ? 'text-primary' : 'text-text-primary'}`}>{label}</p>
                  <p className="text-[11px] text-text-muted">{desc}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  form.permissions[key] ? 'bg-primary border-primary' : 'border-border'
                }`}>
                  {form.permissions[key] && <i className="fa-solid fa-check text-white text-[9px]" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading} className="btn-primary w-full">
          {loading ? <i className="fa-solid fa-spinner animate-spin mr-2" /> : null}
          {isEdit ? 'Save Changes' : 'Create Staff Account'}
        </button>
      </div>
    </div>
  );
}

function ResetPassModal({ staff, onClose }) {
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const handleReset = async () => {
    if (password.length < 6) { toast.error('Password must be 6+ characters'); return; }
    setLoading(true);
    try {
      await resetAdminStaffPass(staff._id, { password });
      toast.success('Password reset!');
      onClose();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-xs p-5 space-y-4">
        <h3 className="text-base font-bold">Reset Password — {staff.name}</h3>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          className="input" placeholder="New password (6+ chars)" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-border text-sm text-text-secondary">Cancel</button>
          <button onClick={handleReset} disabled={loading} className="flex-1 btn-primary">
            {loading ? 'Resetting...' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminStaffPage() {
  const [staffList, setStaffList] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(null); // null | 'create' | staff object
  const [resetModal,setResetModal]= useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminStaff();
      setStaffList(res.staff || []);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await deleteAdminStaff(id);
      toast.success('Staff deleted');
      load();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const handleToggle = async (staff) => {
    try {
      await updateAdminStaff(staff._id, { isActive: !staff.isActive });
      toast.success(staff.isActive ? 'Staff deactivated' : 'Staff activated');
      load();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-black text-text-primary font-heading">Admin Staff</h1>
            <p className="text-sm text-text-muted">Create accounts and manage section access for your team</p>
          </div>
          <button onClick={() => setModal('create')} className="btn-primary flex items-center gap-2">
            <i className="fa-solid fa-plus" />
            <span className="hidden sm:inline">Add Staff</span>
          </button>
        </div>

        {/* Login URL info */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
          <i className="fa-solid fa-circle-info text-blue-500 text-sm mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-blue-800">Staff Login URL</p>
            <p className="text-[11px] text-blue-600 mt-0.5">
              Staff log in from the same URL as you: <span className="font-mono">/admin/[slug]/login</span> — they choose "Staff Login"
            </p>
          </div>
        </div>

        {loading ? <InlineLoader text="Loading staff..." /> : staffList.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto border border-border">
              <i className="fa-solid fa-user-shield text-text-muted text-2xl" />
            </div>
            <p className="text-text-muted text-sm">No staff accounts yet</p>
            <button onClick={() => setModal('create')} className="btn-primary mx-auto flex items-center gap-2">
              <i className="fa-solid fa-plus" />Add First Staff
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {staffList.map(s => (
              <div key={s._id} className={`bg-white rounded-xl border border-border p-4 ${!s.isActive ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
                    <i className="fa-solid fa-user-tie text-primary text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-text-primary">{s.name}</p>
                      <span className="text-[10px] font-mono text-text-muted bg-surface border border-border px-1.5 py-0.5 rounded-full">
                        @{s.username}
                      </span>
                      {!s.isActive && (
                        <span className="text-[10px] font-bold text-danger bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">Inactive</span>
                      )}
                    </div>
                    {/* Permission badges */}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Object.entries(PERMISSION_LABELS).map(([key, { label, icon }]) => (
                        s.permissions[key] ? (
                          <span key={key} className="text-[10px] font-medium text-primary bg-primary-light border border-primary/20 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                            <i className={`fa-solid ${icon} text-[8px]`} />
                            {label}
                          </span>
                        ) : null
                      ))}
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setModal(s)} title="Edit"
                      className="w-8 h-8 rounded-lg bg-surface border border-border flex items-center justify-center">
                      <i className="fa-solid fa-pen text-text-secondary text-xs" />
                    </button>
                    <button onClick={() => setResetModal(s)} title="Reset password"
                      className="w-8 h-8 rounded-lg bg-surface border border-border flex items-center justify-center">
                      <i className="fa-solid fa-key text-accent text-xs" />
                    </button>
                    <button onClick={() => handleToggle(s)} title={s.isActive ? 'Deactivate' : 'Activate'}
                      className="w-8 h-8 rounded-lg bg-surface border border-border flex items-center justify-center">
                      <i className={`fa-solid ${s.isActive ? 'fa-eye-slash' : 'fa-eye'} text-text-muted text-xs`} />
                    </button>
                    <button onClick={() => handleDelete(s._id, s.name)} title="Delete"
                      className="w-8 h-8 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center">
                      <i className="fa-solid fa-trash text-danger text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <StaffModal
          staff={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
      {resetModal && (
        <ResetPassModal staff={resetModal} onClose={() => { setResetModal(null); load(); }} />
      )}
    </AdminLayout>
  );
}
